/** @jsxImportSource theme-ui */
import React, { useState, useCallback } from 'react';
import {
  Flex,
  PlaceholderMessage,
  useToastContext,
} from '@infotrack/zenith-ui';
import { TopFiveItem } from './topFiveItem';
import { TopFiveTeamMembers } from './topFiveTeamMembers';
import { TopFiveItemHistory } from './topFiveItemHistory';
import { TopFiveComments } from './topFiveComments';
import { containerStyles } from './topFiveItemsList.styles';
import type {
  ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdExtendedTopFiveItemDto,
  ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto,
} from '@/api/models';
import type { TopFiveItemType } from '@/api/types/topFiveItemType';

type PendingItemChange = {
  topFiveItemId: number;
  completion?: number;
  status?: string;
  result?: string;
  weight?: number;
};

// Type guard to check if item has the extended staff data structure
const isExtendedTopFiveItem = (
  item: TopFiveItemType
): item is ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdExtendedTopFiveItemDto => {
  return (
    'staffWithSameTopFiveItem' in item &&
    item.staffWithSameTopFiveItem != null &&
    item.staffWithSameTopFiveItem.length > 0 &&
    item.staffWithSameTopFiveItem[0] != null &&
    'staff' in item.staffWithSameTopFiveItem[0]
  );
};

type TopFiveItemsListProps = {
  items: TopFiveItemType[];
  onItemChange: (itemId: number, changes: Partial<PendingItemChange>) => void;
  permissions?: ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto;
  /**
   * @default false
   */
  showActions?: boolean;
  /**
   * @default "No TOP 5 available"
   */
  emptyTitle?: string;
  /**
   * @default "TOP 5 items will appear here when they are added"
   */
  emptySubtitle?: string;
  /**
   * @default false
   */
  isInteractive?: boolean;
  topFiveId?: number;
};

export const TopFiveItemsList = ({
  items,
  onItemChange,
  permissions,
  showActions = false,
  emptyTitle = 'No TOP 5 available',
  emptySubtitle = 'TOP 5 items will appear here when they are added',
  isInteractive = false,
  topFiveId,
}: TopFiveItemsListProps) => {
  const toastContext = useToastContext();
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [expansionType, setExpansionType] = useState<
    'team' | 'history' | 'comment' | null
  >(null);

  const handleToggleExpansion = useCallback((itemId: number) => {
    setExpandedItemId(prev => (prev === itemId ? null : itemId));
  }, []);

  const handleActionClick = useCallback(
    (action: 'comment' | 'history' | 'group', item: TopFiveItemType) => {
      switch (action) {
        case 'group':
          if (item.topFiveItemId) {
            const newExpandedIdTeam =
              expandedItemId === item.topFiveItemId && expansionType === 'team'
                ? null
                : item.topFiveItemId;
            setExpandedItemId(newExpandedIdTeam);
            setExpansionType(newExpandedIdTeam ? 'team' : null);
          }
          break;
        case 'history':
          if (item.topFiveItemId) {
            const newExpandedIdHistory =
              expandedItemId === item.topFiveItemId &&
              expansionType === 'history'
                ? null
                : item.topFiveItemId;
            setExpandedItemId(newExpandedIdHistory);
            setExpansionType(newExpandedIdHistory ? 'history' : null);
          }
          break;
        case 'comment':
          if (item.topFiveItemId) {
            const newExpandedIdComment =
              expandedItemId === item.topFiveItemId &&
              expansionType === 'comment'
                ? null
                : item.topFiveItemId;
            setExpandedItemId(newExpandedIdComment);
            setExpansionType(newExpandedIdComment ? 'comment' : null);
          }
          break;
        default:
          toastContext.error({
            title: 'Error',
            message: 'Unrecognized action',
          });
      }
    },
    [expandedItemId, expansionType, toastContext]
  );

  return (
    <Flex sx={containerStyles} role="list">
      {items.length > 0 ? (
        items.map((item, index) => {
          const isExpanded = expandedItemId === item.topFiveItemId;
          const hasTeamMembers =
            item.staffWithSameTopFiveItem &&
            item.staffWithSameTopFiveItem.length > 0;
          const isExtended = isExtendedTopFiveItem(item);
          return (
            <React.Fragment key={`${item.completion}-${item.topFiveItemId}`}>
              <TopFiveItem
                item={item}
                itemNumber={index + 1}
                onItemChange={changes =>
                  item.topFiveItemId &&
                  onItemChange(item.topFiveItemId, changes)
                }
                permissions={permissions}
                showActions={showActions}
                onActionClick={handleActionClick}
                isInteractive={isInteractive}
                isExpanded={isExpanded}
                onToggleExpansion={handleToggleExpansion}
              />
              {isExpanded &&
                expansionType === 'team' &&
                hasTeamMembers &&
                isExtended &&
                item.staffWithSameTopFiveItem && (
                  <TopFiveTeamMembers
                    staffWithSameTopFiveItem={item.staffWithSameTopFiveItem}
                    currentItemName={item.name || ''}
                  />
                )}
              {isExpanded && expansionType === 'history' && (
                <TopFiveItemHistory item={item} />
              )}
              {isExpanded && expansionType === 'comment' && (
                <TopFiveComments
                  item={item}
                  permissions={permissions}
                  topFiveId={topFiveId}
                />
              )}
            </React.Fragment>
          );
        })
      ) : (
        <PlaceholderMessage title={emptyTitle} subtitle={emptySubtitle} />
      )}
    </Flex>
  );
};
